import * as CSSTransition from "react-transition-group/CSSTransition";

import { IButtonStyles } from "@fluentui/react";
import { IVerticalDividerClassNames } from "@fluentui/react/lib/Divider";
import {
  HighContrastSelector,
  ITheme,
  mergeStyleSets,
} from "@fluentui/react/lib/Styling";
import { memoizeFunction } from "@fluentui/react/lib/Utilities";

import {
  commonHighContrastNoAdjustStyles,
  commonReducedMotionStyles,
  getDividerClassNames,
  getModernUxExperimentPreference,
  getStandardControlSemanticColorsWithTheme,
  prefersReducedMotionDurationMs,
  StandardControlSemanticColors,
} from "@1js/acui-common";

import { ribbonModeToggleId } from ".";
import {
  getRibbonBottomBarStylingConstants,
  modernUxRibbonStylingConstants,
} from "../../ribbonStylingConstants";
import { RibbonMode } from "../Ribbon/Ribbon.Props";
import { getRibbonBottomBarWrapperShadowStyles } from "./RibbonBottomBarWrapperStyles";
import { RibbonBottomBarStylingConstants } from "../../../..";

export interface RibbonBottomBarClassNames {
  root: string;
  groupContainer: string;
  tabContentContainer: string;
  bottomBarContainer: string;
  chevronContainer: string;
  resizeGroup: string;
}

const bottomBarEnterTransitionDurationMs = 150;
const bottomBarExitTransitionDurationMs = 80;
const bottomBarOpacityTransitionDurationMs = 80;
const ribbonModeFadeOutTransitionDurationMs = 50;

export const bottomBarTransitionDurationMs = 150;
export const modeSwitchTransitionClassNames: CSSTransition.CSSTransitionClassNames =
  {
    enter: "ribbonMode-enter",
    enterActive: "ribbonMode-enter-active",
    exit: "ribbonMode-exit",
    exitActive: "ribbonMode-exit-active",
  };

export const chevronWidth = 20;
export const tabSwitchTransitionClassNames: CSSTransition.CSSTransitionClassNames =
  {
    enter: "tabContent-enter",
    enterActive: "tabContent-enter-active",
    exit: "tabContent-exit",
    exitActive: "tabContent-exit-active",
  };

export const getRibbonHeightTransition = (
  reduceAnimations?: boolean
): string => {
  return `height ${
    reduceAnimations
      ? prefersReducedMotionDurationMs
      : bottomBarEnterTransitionDurationMs
  }ms cubic-bezier(0.1, 0.9, 0.2, 1)`;
};

// Options to pass in to getRibbonBottomBarClassNames to
// properly style the bottom bar
export interface RibbonBottomBarClassNamesOptions {
  // mode the ribbon is in, MLR or SLR
  ribbonMode: RibbonMode;
  // if the chevron to switch ribbonModes is visible
  isModeChevronVisible?: boolean;
  // if we want to reduce animations for users
  reduceAnimations?: boolean;
}

export const tabContentContainerPadding = 8;

export const getRibbonBottomBarClassNames = memoizeFunction(
  (
    theme: ITheme,
    styleOptions: RibbonBottomBarClassNamesOptions
  ): RibbonBottomBarClassNames => {
    const bottomBarWrapperStyles = getRibbonBottomBarWrapperShadowStyles(
      theme,
      styleOptions.ribbonMode
    );
    const bottomBarHeight = getRibbonBottomBarStylingConstants(
      styleOptions.ribbonMode
    ).bottomBarHeight;

    // The ribbon mode toggle, when expanded, needs to override the current fill of Highlight Text
    // to show in highcontrast.  The selector needs to be at the level higher than the button to accurately apply the
    // fill
    const toggleModeSelector = `button[id='${ribbonModeToggleId}'][aria-expanded='true'] svg > path.OfficeIconColors_HighContrast`;
    const toggleModesHoverSelector = `button[id='${ribbonModeToggleId}'][aria-expanded='true'] :hover svg > path.OfficeIconColors_HighContrast`;
    return mergeStyleSets({
      root: [
        {
          ...bottomBarWrapperStyles,
          display: "flex",
          justifyContent: "space-between",
          overflow: "hidden",
          transition: getRibbonHeightTransition(styleOptions.reduceAnimations),
          selectors: {
            [HighContrastSelector]: {
              borderBottom: "1px solid",
              selectors: {
                [toggleModeSelector]: {
                  fill: "ButtonText",
                },
                [toggleModesHoverSelector]: {
                  fill: "HighlightText",
                },
              },
            },
          },
        },
        commonReducedMotionStyles,
      ],
      tabContentContainer: {
        width: "100%",
        paddingLeft: tabContentContainerPadding,
        overflow: "hidden",
      },
      bottomBarContainer: [
        {
          display: "flex",
          whiteSpace: "nowrap",
          selectors: {
            [`&.${tabSwitchTransitionClassNames.enter}`]: {
              position: "absolute",
              transform: `translateX(-7px)`,
              opacity: 0.5,
            },
            [`&.${tabSwitchTransitionClassNames.enterActive}`]: {
              transition: `opacity ${bottomBarOpacityTransitionDurationMs}ms linear, transform ${bottomBarEnterTransitionDurationMs}ms cubic-bezier(0.1, 0.9, 0.2, 1)`,
              transform: `translateX(0)`,
              opacity: 1,
            },
            [`&.${tabSwitchTransitionClassNames.exit}`]: {
              transform: `translateX(0)`,
              opacity: 0.5,
            },
            [`&.${tabSwitchTransitionClassNames.exitActive}`]: {
              transition: `opacity ${bottomBarOpacityTransitionDurationMs}ms linear, transform ${bottomBarExitTransitionDurationMs}ms cubic-bezier(0.9, 0.1, 1, 0.2)`,
              transform: `translateX(17px)`,
              opacity: 0,
            },
            [`&.${modeSwitchTransitionClassNames.enter}`]: {
              transition: `opacity ${bottomBarOpacityTransitionDurationMs}ms linear, transform ${bottomBarEnterTransitionDurationMs}ms cubic-bezier(0.1, 0.64, 0.2, 1)`,
              opacity: 0.5,
              position: "absolute",
              transform:
                styleOptions.ribbonMode === "SingleLine"
                  ? "scale(1.05)"
                  : "scale(0.95)",
            },
            [`&.${modeSwitchTransitionClassNames.enterActive}`]: {
              opacity: 1,
              transform: "scale(1)",
            },
            [`&.${modeSwitchTransitionClassNames.exit}`]: {
              transition: `opacity ${ribbonModeFadeOutTransitionDurationMs}ms linear, transform ${bottomBarExitTransitionDurationMs}ms cubic-bezier(0.1, 0.64, 0.2, 1)`,
              opacity: 0.5,
            },
            [`&.${modeSwitchTransitionClassNames.exitActive}`]: {
              opacity: 0,
            },
          },
        },
        commonReducedMotionStyles,
      ],
      groupContainer: {
        display: "inline-block",
        verticalAlign: "top",
        height: bottomBarHeight,
      },
      chevronContainer: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        alignItems: "flex-end",
        lineHeight: "16px",
        height: bottomBarHeight,
        // Ensure that tabContentContainer slides in/out behind the mode chevron
        zIndex: 1,
        backgroundColor: bottomBarWrapperStyles.backgroundColor,
      },
      resizeGroup: {
        width: "100%",
        display: "inline-flex",
        overflow: "hidden",
      },
    });
  }
);

export const getVerticalDividerWidthSLR = () => {
  const stylingConstants = getRibbonBottomBarStylingConstants("SingleLine");
  return (
    stylingConstants.groupDividerPadding.left +
    stylingConstants.groupDividerPadding.right +
    1
  );
};

export const getRibbonGroupDividerClassNames = memoizeFunction(
  (
    theme: ITheme,
    bottomBarConstants: RibbonBottomBarStylingConstants,
    wrapperClass?: string
  ): IVerticalDividerClassNames => {
    const { bottomBarHeight, groupDividerPadding } = bottomBarConstants;
    const paddingTop = getModernUxExperimentPreference()
      ? modernUxRibbonStylingConstants.dividerPaddingTop
      : groupDividerPadding.top;
    const paddingBottom = getModernUxExperimentPreference()
      ? modernUxRibbonStylingConstants.dividerPaddingBottom
      : groupDividerPadding.bottom;
    const borderRadius = getModernUxExperimentPreference()
      ? modernUxRibbonStylingConstants.borderRadius
      : 0;
    return mergeStyleSets(getDividerClassNames(theme), {
      wrapper: [
        {
          height: bottomBarHeight - paddingTop - paddingBottom,
          paddingRight: groupDividerPadding.right,
          paddingLeft: groupDividerPadding.left,
          paddingTop,
          paddingBottom,
        },
        wrapperClass,
      ],
      divider: [
        {
          borderRadius,
        },
        "ribbon-bottom-bar-divider",
        commonHighContrastNoAdjustStyles,
      ],
    });
  }
);

export const getModeChevronStyles = memoizeFunction(
  (
    mode: RibbonMode,
    theme: ITheme,
    reduceAnimations?: boolean,
    shyRibbonEnabled?: boolean
  ): IButtonStyles => {
    const duration = reduceAnimations
      ? prefersReducedMotionDurationMs
      : bottomBarEnterTransitionDurationMs;
    const standardControlSemanticColors: StandardControlSemanticColors =
      getStandardControlSemanticColorsWithTheme(theme);

    return {
      root: {
        height: 16,
        width: chevronWidth,
        padding: 0,
      },
      icon: [
        {
          height: 12,
          width: 12,
          lineHeight: 12,
          fill: standardControlSemanticColors.textColor,
        },
        !shyRibbonEnabled && {
          transition: `transform ${duration}ms cubic-bezier(0.1, 0.9, 0.2, 1)`,
          transform: mode === "Multiline" ? "rotate(180deg)" : undefined,
        },
        commonReducedMotionStyles,
      ],
    };
  }
);
